// Tek araç canlı snapshot — GET /api/telemetry/vehicle/:vehicleId (15 sn poll).

import { useEffect, useRef, useState, useCallback } from "react";
import { useHttpClient } from "shared/hooks/http-hook";
import { TELEMETRY_THRESHOLDS } from "@routebot/constants";

const REFRESH_MS = (TELEMETRY_THRESHOLDS?.LIVE_POSITION_REFRESH_SECONDS || 15) * 1000;

export const useTelemetryVehicle = ({ vehicleId, enabled = true } = {}) => {
  const { sendRequest } = useHttpClient();
  const [snapshot, setSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchSnapshot = useCallback(async () => {
    if (!vehicleId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/telemetry/vehicle/${vehicleId}`);
      if (!mountedRef.current) return;
      setSnapshot(data?.snapshot ?? null);
      setLastLoadedAt(new Date());
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || "fetch failed");
      setSnapshot(null);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [sendRequest, vehicleId]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled || !vehicleId) {
      setSnapshot(null);
      setError(null);
      return undefined;
    }

    fetchSnapshot();
    timerRef.current = setInterval(fetchSnapshot, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, vehicleId, fetchSnapshot]);

  return { snapshot, isLoading, error, lastLoadedAt, refresh: fetchSnapshot };
};

export default useTelemetryVehicle;
